import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_SPECIAL_EQUIPMENT = 'special-equipment';
export const SPECIAL_EQUIPMENT_CONDITION_ID = 'specialEquipment';
export const FilterConditionSpecialEquipmentPluginType: FilterConditionPluginSpecification<
  string[],
  SpecialEquipmentRecord,
  SpecialEquipmentCondition
> = {
  type: 'specialEquipment',
  category: 'clinic',
  displayName: {
    de: 'Klinik-Ausstattung',
  },
  occurrence: 'single',
  sortingIndex: 8,
  fromObject(
    obj: SpecialEquipmentRecord,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionSpecialEquipmentPlugin(
        obj.includeSpecialEquipmentKeys,
      );
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionSpecialEquipmentPlugin();
  },
  fromCondition(
    jsonObj: SpecialEquipmentCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionSpecialEquipmentPlugin(
        jsonObj.includes,
        jsonObj.excludes,
      );
  },
};

export class FilterConditionSpecialEquipmentPlugin extends FilterConditionPluginImpl<
  string[],
  SpecialEquipmentRecord
> {
  public static override Specification: FilterConditionPluginSpecification<
    string[],
    SpecialEquipmentRecord,
    SpecialEquipmentCondition
  > = FilterConditionSpecialEquipmentPluginType;

  private readonly includeKeys: string[];
  private readonly excludeKeys: string[];

  /**
   * `BdContentCondition.toJSON` serializes an empty include/exclude list as
   * `null` (AS_NULL strategy), so a persisted clinics-filter reads back as
   * `{ includes: [...], excludes: null }`. Default parameters only apply to
   * `undefined`, so the `null` has to be normalized here — otherwise it reaches
   * `clone()` and throws `excludeKeys is not iterable` (BDA-5830).
   */
  constructor(
    includeKeys: string[] | null = [],
    excludeKeys: string[] | null = [],
  ) {
    super(FilterConditionSpecialEquipmentPluginType.type);
    this.includeKeys = includeKeys ?? [];
    this.excludeKeys = excludeKeys ?? [];
  }

  override toCondition(): BdCondition | null {
    return new SpecialEquipmentCondition(this.includeKeys, this.excludeKeys);
  }

  toJSON(): SpecialEquipmentRecord | null {
    return {
      type: this.type,
      includeSpecialEquipmentKeys: this.includeKeys,
    };
  }

  toText(): string | null {
    if (this.includeKeys.length === 1) {
      return 'ein Ausstattungs-Merkmal';
    } else {
      return `${this.includeKeys.length} Ausstattungs-Merkmale`;
    }
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.includeKeys.length === 0) return {};
    return { [FILTER_RESOURCE_SPECIAL_EQUIPMENT]: [...this.includeKeys] };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.includeKeys.map((key) => {
      const name = resolvedNames?.get(key);
      return name ? `${key} — ${name}` : key;
    });
    return { title, items };
  }

  validationToText(): string {
    return this.valid() ? '' : 'Ausstattungs-Merkmale auswählen';
  }

  override toValue(): string[] {
    return this.includeKeys;
  }

  clone(): FilterConditionSpecialEquipmentPlugin {
    return new FilterConditionSpecialEquipmentPlugin(
      [...this.includeKeys],
      [...this.excludeKeys],
    );
  }

  override equals(other: FilterConditionSpecialEquipmentPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includeKeys, other.includeKeys) &&
      BdArrayUtil.equals(this.excludeKeys, other.excludeKeys)
    );
  }
}

export class SpecialEquipmentCondition extends BdContentCondition<string> {
  constructor(includeKeys: string[] = [], excludeKeys: string[] = []) {
    super(SPECIAL_EQUIPMENT_CONDITION_ID, includeKeys, excludeKeys);
  }
}

interface SpecialEquipmentRecord {
  type: string;
  includeSpecialEquipmentKeys: string[];
}
